import './DocumentsBatchEditingExecutor.scss';
import React, { useCallback, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { IDocumentData } from '@models/document/IDocumentData';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { groupRunResults } from '@services/actions/groupOperationRun';
import { DocumentService } from '@services/DocumentService';
import { getFormDataByScheme } from '@utils/documentUtils';
import { FormulaManager } from '@utils/managers/FormulaManager';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { IMailRemindersOptions } from '@molecules/AddMailReminderBlock/AddMailReminderBlock';
import ModalFormBuilder, { IModalFormBuilderProps } from '@molecules/ModalFormBuilder/ModalFormBuilder';
import {
    batchEditingUploadProgress,
    checkBatchEditingSubmit,
    createBatchEditingMailReminders,
} from '@organisms/actions/batchEditing/batchEditingSubmit';
import { useBatchEditingFormData } from '@organisms/actions/batchEditing/useBatchEditingFormData';
import { IDocumentsBatchEditingExecutorProps } from './IDocumentsBatchEditingExecutorProps';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

const defaultSuccessText = 'Действие успешно выполнено';

export const DocumentsBatchEditingExecutorModal: React.FC<IDocumentsBatchEditingExecutorProps> = (
    props: IDocumentsBatchEditingExecutorProps,
) => {
    const [loading, setLoading] = useState<boolean>();
    const [errorText, setErrorText] = useState<string>();
    const [successText, setSuccessText] = useState<string>();
    const [success, setSuccess] = useState<boolean>(false);
    const [warningText, setWarningText] = useState<string>();
    const [showOkButton, setShowOkButton] = useState<boolean>(true);
    const [uploadProgress, setUploadProgress] = useState<number>();
    const [submitButtonLabel, setSubmitButtonLabel] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [hasFiles, setHasFiles] = useState<boolean>(false);

    const methods = useForm<IDocumentData>({
        mode: 'onBlur', // "onChange"
    });
    const hasEditedFields = useUnsavedFormInput(methods);
    const service = new DocumentExecutionService(props.docId);

    const { form, fieldsData, attachesCreateModel, mailRemindersBlockModel } = useBatchEditingFormData({
        docId: props.docId,
        actionKey: props.actionKey,
        attachFiles: props.attachFiles,
        addMailReminders: props.addMailReminders,
        showCurrentValues: props.showCurrentValues,
        virtualFields: props.virtualFields,
        methods: methods,
        getFormBuilderScheme: () => service.getFormBuilderScheme(props.actionKey),
        getDataByAction: () =>
            DocumentService.getDataByAction(props.actionKey, props.docId, undefined, false, props.showCurrentValues),
        onError: setErrorText,
    });

    /**
     * Обрабатывает подстановки
     * @param expression
     * @returns
     */
    const processSubstitutionsAsync = async (expression: string): Promise<string | undefined> => {
        const mng = new FormulaManager(expression);
        mng.Init();
        const fields = mng.GetFields();
        if (fields.length > 0) {
            const response = await DocumentService.getFormData(props.docId, mng.GetFields());
            return mng.ReplaceFormulaValues(
                false,
                false,
                response.data.fields.map((field) => field.value),
                true,
            );
        } else {
            return Promise.resolve(expression);
        }
    };

    /**
     * Обрабатывает post-action действия
     * @param actions
     */
    const processPostActions = (actions?: any[]) => {
        if (actions) {
            actions.forEach(async (action) => {
                switch (action.type) {
                    case 'openUrlInNewTab': {
                        const url = await processSubstitutionsAsync(action.url);
                        window.open(url, '_blank', 'noopener,noreferrer');
                        break;
                    }

                    default:
                        break;
                }
            });
        }
    };

    const onSubmit = useCallback(
        async (data: FieldValues, files?: ISelectedFiles, mailReminderOptions?: IMailRemindersOptions) => {
            setErrorText(undefined);
            setWarningText(undefined);
            setSuccessText(undefined);
            setSuccess(false);
            setLoading(true);

            const check = await checkBatchEditingSubmit({
                files: files,
                attachFiles: props.attachFiles,
                attachRequired: props.attachRequired,
                attachRequiredValidation: props.attachRequiredValidation,
                form: form,
                fieldsData: fieldsData,
                methods: methods,
            });
            if (check.warning || check.invalid) {
                if (check.warning) setWarningText(check.warning);
                setLoading(false);
                return;
            }

            const val = getFormDataByScheme(form?.view?.rows?.row!, data, props.onlyEditValues, form?.changeActions);
            const hasFilesToUpload = !!files?.files?.length;
            setIsSubmitting(hasFilesToUpload);
            setSubmitButtonLabel(hasFilesToUpload ? 'Подготавливаю файлы...' : undefined);

            if (hasFilesToUpload) {
                setUploadProgress(0);
            }

            // Сохранение данных
            service
                .saveDocumentsBatchEditingData(
                    props.docId,
                    props.actionKey,
                    val,
                    files,
                    hasFilesToUpload ? batchEditingUploadProgress(setUploadProgress, setSubmitButtonLabel) : undefined,
                )
                .then(async (result) => {
                    // Выполнение по документам: итог по каждому покажет окно групповой операции,
                    // напоминания — только сохранённым
                    const itemResults = groupRunResults(result.data);
                    const savedIds = itemResults
                        ? itemResults
                              .filter((item) => item.success)
                              .map((item) => item.id)
                              .join(',')
                        : props.docId;
                    if (!itemResults && result.data.warnings && result.data.warnings.length > 0) {
                        setWarningText(result.data.warnings.join(', '));
                        setShowOkButton(false);
                    } else {
                        // Создание напоминаний (выполняется только после успешного сохранения)
                        if (mailReminderOptions && mailRemindersBlockModel && savedIds) {
                            await createBatchEditingMailReminders(
                                savedIds,
                                props.actionKey,
                                val,
                                mailRemindersBlockModel,
                                mailReminderOptions,
                            );
                        }
                        processPostActions(props.postActions);
                        if (props.showSuccessText && !itemResults) {
                            setSuccessText(props.successText || defaultSuccessText);
                            setSuccess(true);
                            setShowOkButton(false);
                        } else {
                            props.completeHandler && props.completeHandler(true);
                        }
                    }
                })
                .catch((error) => setErrorText(error))
                .finally(() => {
                    setLoading(false);
                    setIsSubmitting(false);
                    setUploadProgress(undefined);
                    setSubmitButtonLabel(undefined);
                });
        },
        [form, fieldsData, methods],
    );

    const messageBoxProp: IModalFormBuilderProps = {
        header: props.title,
        message: props.message,
        showOkButton: !!fieldsData && showOkButton,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: success ? 'Закрыть' : props.cancelButtonText,
        size: props.modalSize,
        loading: (!!loading && !isSubmitting) || (!fieldsData && !errorText),
        isSubmitting: isSubmitting,
        // `loading` — всё сохранение (проверки, запись, напоминания); загрузку формы можно бросить.
        closeDisabled: !!loading,
        hasUnsavedInput: () => !success && (hasFiles || hasEditedFields()),
        onFilesChanged: (files) => setHasFiles(files.files.length > 0),
        onSubmit: onSubmit,
        onCancel: () => props.completeHandler?.(success),
        errorText: errorText,
        successText: successText,
        warningText: warningText,
        formMethods: methods,
        fields: fieldsData ?? {},
        changeActions: form?.changeActions,
        rows: form?.view,
        createAttachModel: attachesCreateModel,
        mailRemindersBlockModel: mailRemindersBlockModel,
        showContent: !!fieldsData && showOkButton && !success,
        docId: props.docId,
        submitProgress: uploadProgress,
        submitProgressLabel: submitButtonLabel,
    };

    return <ModalFormBuilder {...messageBoxProp} />;
};
